import { useEffect, useState, useSyncExternalStore } from 'react';

/**
 * The app shell's two notices, on every screen (team, portal, signed out):
 * - offline: the device lost its connection, so nothing can be saved or loaded until it is back;
 * - update: a new version took over the service worker (public/sw.js activates at once), so the page's code is old: Reload.
 * Pinned under the header: over the page, under the header (its account menu opens over the notice) and under any sheet.
 */
let ready = false;
const subs = new Set<() => void>();
const say = () => subs.forEach((s) => s());

/** Watch the service worker: a new one taking control of a page that already had one = a new version is ready. */
export function watchForUpdates(sw: ServiceWorkerContainer, reg?: ServiceWorkerRegistration) {
  const had = !!sw.controller;
  sw.addEventListener('controllerchange', () => {
    if (!had || ready) return;
    ready = true;
    say();
  });
  // A tab left open for days asks again when it comes back to the front (the browser's own check is once a day at most).
  if (reg) document.addEventListener('visibilitychange', () => { if (document.visibilityState === 'visible') void reg.update().catch(() => {}); });
}

function useOnline() {
  const [on, setOn] = useState(() => (typeof navigator === 'undefined' ? true : navigator.onLine !== false));
  useEffect(() => {
    const up = () => setOn(true);
    const down = () => setOn(false);
    addEventListener('online', up);
    addEventListener('offline', down);
    return () => { removeEventListener('online', up); removeEventListener('offline', down); };
  }, []);
  return on;
}

export function ShellNotices({ reload = () => location.reload() }: { reload?: () => void }) {
  const online = useOnline();
  const update = useSyncExternalStore((s) => (subs.add(s), () => subs.delete(s)), () => ready);
  return (
    <div role="status" className="pointer-events-none fixed inset-x-0 top-[calc(env(safe-area-inset-top)+64px)] z-20 flex flex-col items-center gap-2 px-4">
      {!online ? (
        <div data-testid="offline-notice" className="animate-rise pointer-events-auto flex min-h-11 w-full max-w-[440px] items-center gap-3 rounded-2xl border border-line-2 bg-panel px-4 py-2 text-[14px] text-ink shadow-2">
          <span className="h-2 w-2 shrink-0 rounded-full bg-warn" aria-hidden="true" />
          <span className="flex-1">You’re offline. Nothing can be saved or loaded until the connection is back.</span>
        </div>
      ) : null}
      {update ? (
        <div data-testid="update-notice" className="animate-rise pointer-events-auto flex min-h-11 w-full max-w-[440px] items-center gap-3 rounded-2xl border border-line-2 bg-panel px-4 py-2 text-[14px] text-ink shadow-2">
          <span className="flex-1">A new version is ready.</span>
          <button type="button" className="btn-primary btn-sm" onClick={reload} data-testid="update-reload">Reload</button>
        </div>
      ) : null}
    </div>
  );
}
